/**
 * Projects hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { Project, CreateProjectInput, UpdateProjectInput } from '@aelita/shared';

interface ProjectFilters {
  page?: number;
  limit?: number;
  status?: string;
  type?: string;
  clientId?: string;
  search?: string;
}

export function useProjects(filters: ProjectFilters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });

  return useQuery({
    queryKey: ['projects', filters],
    queryFn: async () => {
      const response = await api.get<Project[]>(`/projects?${params.toString()}`);
      return response;
    },
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const response = await api.get<Project>(`/projects/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectInput) => {
      const response = await api.post<Project>('/projects', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Also invalidate client projects if this project belongs to a client
      if (variables.clientId) {
        queryClient.invalidateQueries({ queryKey: ['client', variables.clientId, 'projects'] });
        queryClient.invalidateQueries({ queryKey: ['client', variables.clientId] });
      }
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProjectInput }) => {
      const response = await api.put<Project>(`/projects/${id}`, data);
      return response.data;
    },
    onSuccess: (_, { id, data }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      // Invalidate client queries in case clientId changed
      if (data.clientId) {
        queryClient.invalidateQueries({ queryKey: ['client', data.clientId, 'projects'] });
        queryClient.invalidateQueries({ queryKey: ['client', data.clientId] });
      }
      // Also invalidate all clients in case we moved from one client to another
      queryClient.invalidateQueries({ queryKey: ['client'] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/projects/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Invalidate all client queries since we don't know which client this project belonged to
      queryClient.invalidateQueries({ queryKey: ['client'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useArchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<Project>(`/projects/${id}/archive`, {});
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useUnarchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<Project>(`/projects/${id}/unarchive`, {});
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useProjectTasks(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project', projectId, 'tasks'],
    queryFn: async () => {
      const response = await api.get<unknown[]>(`/projects/${projectId}/tasks`);
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useProjectTeam(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project', projectId, 'team'],
    queryFn: async () => {
      const response = await api.get<unknown[]>(`/projects/${projectId}/team`);
      return response.data;
    },
    enabled: !!projectId,
  });
}
